import { Button, Typography } from '@breetometer/components';
import { BankIcon, ShareIcon } from 'BreetAssets';
import { Loader } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	type CURRENCYCODETYPE,
	handleApiError,
	shareOrCopyText,
	showToast,
	viewHelpArticle,
} from 'BreetHelpers';
import { useModalApi } from 'BreetHooks';
import {
	selectFiatMeta,
	selectMeta,
	useAppSelector,
	useGenerateVirtualAccountMutation,
	useGetBeneficiariesQuery,
	useGetUserQuery,
} from 'BreetRedux';
import { useCallback } from 'react';

import { AccountDetailsItem } from './AccountDetailsItem';

const FiatFundingMethod = () => {
	const { handleOpenModal, openModalDirect } = useModalApi();

	const { data: userData, isFetching: isUserFetching } = useGetUserQuery(undefined, alwaysRefetchApiQueryOptions);

	const { data: beneficiaryData, isFetching: isBeneficiaryFetching } = useGetBeneficiariesQuery(
		undefined,
		alwaysRefetchApiQueryOptions
	);

	const meta = useAppSelector(selectMeta);
	const fiatMeta = useAppSelector(selectFiatMeta);

	const isInitDataLoading = isUserFetching || isBeneficiaryFetching;

	const vbaAccount = beneficiaryData?.data?.find((beneficiary) => beneficiary.type === 'virtual_account');

	const [generateVbafn, { isLoading: isGenerateVbaLoading }] = useGenerateVirtualAccountMutation();

	const generateVirtualBankAccount = useCallback(() => {
		if (!userData?.data) {
			showToast('error', 'User data not found');
			return;
		}

		if (!userData.data.hasDirectorBVN) {
			openModalDirect({ id: 'bvnValidation' });
			return;
		}

		generateVbafn({
			currency: 'ngn',
		})
			.unwrap()
			.catch(handleApiError);
	}, [generateVbafn, openModalDirect, userData?.data]);

	const isMetaLoaded = meta != null;
	const isVirtualAccountEnabled =
		isMetaLoaded && !!meta.settings.virtualAccountMeta?.[fiatMeta.currency.toLowerCase() as Lowercase<CURRENCYCODETYPE>]?.enabled;

	const vbaNotAvailable = fiatMeta.currency !== 'NGN' || (isMetaLoaded && !isVirtualAccountEnabled);

	if (isInitDataLoading) {
		return <Loader />;
	}

	return (
		<>
			{!vbaAccount ? (
				<div className='fiat-funding-method'>
					<div className='vba bg-primary-400 border-dark-navy'>
						<BankIcon variant='fund-wallet' />
						<Typography
							as='h4'
							className='color-neutral-50'
						>
							Generate Virtual Bank Account
						</Typography>
						<Typography className='color-primary-100'>
							Generate your permanent business account details to top up your Naira wallet anytime.
						</Typography>
						<Button
							variant='secondary'
							className='theme-background-none'
							onClick={
								vbaNotAvailable
									? handleOpenModal({
											id: 'alert',
											metadata: {
												title: 'Virtual Account Unavailable',
												description: 'Virtual account generation is currently unavailable. Please try again later.',
											},
										})
									: generateVirtualBankAccount
							}
							isLoading={isGenerateVbaLoading || !isMetaLoaded}
						>
							Continue
						</Button>
					</div>
				</div>
			) : (
				<div className='account-details-container'>
					<div className='label-section'>
						<Typography className='color-primary-100'>Transfer to this account to fund your Naira wallet</Typography>
					</div>

					<div className='account-details bg-primary-600 border-dark-navy'>
						<div className='account-details-list'>
							<AccountDetailsItem
								label='Account name'
								value={vbaAccount.accountName}
								loading={isBeneficiaryFetching}
							/>

							<AccountDetailsItem
								label='Account number'
								value={vbaAccount.accountNumber}
								loading={isBeneficiaryFetching}
							/>

							<AccountDetailsItem
								label='Bank name'
								value={vbaAccount.bankName}
								loading={isBeneficiaryFetching}
							/>
						</div>

						<div className='share-details'>
							<Button
								trailingIcon={<ShareIcon variant='button' />}
								className='theme-background-none'
								onClick={shareOrCopyText({
									title: 'Fund your Naira wallet',
									text:
										`Account Name: ${vbaAccount.accountName}\n` +
										`Account Number: ${vbaAccount.accountNumber}\n` +
										`Bank: ${vbaAccount.bankName}`,
									copyMessage: 'Account details copied',
								})}
							>
								Share Details
							</Button>
						</div>

						<div className='note'>
							<Typography as='h6'>Please Read Carefully </Typography>
							<ul>
								<li>1.5% funding fee applied</li>
								<li>
									Sender's name must match your account name to avoid top-up failure.{' '}
									<Typography
										as='a'
										onClick={viewHelpArticle('15830205')}
									>
										Learn more
									</Typography>
								</li>
							</ul>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default FiatFundingMethod;
